import { getSubdomain } from "../../utils/getSubdomain/subdomain";
import { createApiClient } from "../apiService";
import { useToken } from "../tokenUtils";

import { SuccesType } from "../review/reviewTypes";
import {
    assignAssociatedEditorTypes,
    editorTypes,
} from "./reminderTypes";

const apiClient = createApiClient("");
const domain = getSubdomain();

const handlePutRequest = async (url: string, data: any, token?: string): Promise<any> => {
    const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
    };
    
    const res = await apiClient.put(url, data, { headers });
    return res.data;
};

const handlePatchRequest = async (url: string, data: any, token?: string): Promise<any> => {
    const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
    };
    
    const res = await apiClient.patch(url, data, { headers });
    return res.data;
};

// Funciones existentes
const editEditor = async ({ data, slug }: { data: editorTypes; slug: string }): Promise<SuccesType> => {
    const token = useToken();
    const res = await handlePatchRequest(`${domain}/editor/${slug}`, data, token);
    return res;
};

const assignAssociatedEditor = async (data: assignAssociatedEditorTypes): Promise<SuccesType> => {
    if (!data.article_slug || !data.editor_slug) {
        throw new Error("Faltan campos requeridos para construir el path");
    }
    const token = useToken();
    
    const path = `${domain}/editor/articles/${data.article_slug}/assign/${data.editor_slug}?files_visible=${data.visible_files}&message=${data.message}`;

    const res = await handlePutRequest(path, {}, token);
    return res as SuccesType;
};

const sendToPublication = async (article_slug: string): Promise<SuccesType> => {
    const token = useToken();
    if (!token) {
        throw new Error('Falta el token.');
    }
    const res = await handlePutRequest(`${domain}/editor/articles/${article_slug}/send_to_publication`, {}, token);
    return res as SuccesType;
};

// Nuevas funciones de Reminder
const applyAuthorReminder = async (article_slug: string): Promise<SuccesType> => {
    if (!article_slug) {
        throw new Error("Falta el article_slug");
    }
    const token = useToken();
    const path = `${domain}/reminder/article/${article_slug}/author`;

    const res = await handlePutRequest(path, {}, token);
    return res as SuccesType;
};

const applyReviewReminder = async (article_slug: string, review_id: number): Promise<SuccesType> => {
    if (!article_slug || review_id === undefined) {
        throw new Error("Faltan campos requeridos: article_slug y review_id");
    }
    const token = useToken();
    const path = `${domain}/reminder/article/${article_slug}/review/${review_id}`;

    const res = await handlePutRequest(path, {}, token);
    return res as SuccesType;
};

const unexpireArticle = async (article_slug: string): Promise<SuccesType> => {
    if (!article_slug) {
        throw new Error("Falta el article_slug");
    }
    const token = useToken();
    const path = `${domain}/reminder/${article_slug}/unexpire`;

    const res = await handlePutRequest(path, {}, token);
    return res as SuccesType;
};

const unexpireReview = async (article_slug: string, review_id: number): Promise<SuccesType> => {
    if (!article_slug || review_id === undefined) {
        throw new Error("Faltan campos requeridos: article_slug y review_id");
    }
    const token = useToken();
    const path = `${domain}/reminder/${article_slug}/unexpire/${review_id}`;

    const res = await handlePutRequest(path, {}, token);
    return res as SuccesType;
};

const rejectReview = async (article_slug: string, review_id: number): Promise<SuccesType> => {
    if (!article_slug || review_id === undefined) {
        throw new Error("Faltan campos requeridos: article_slug y review_id");
    }
    const token = useToken();
    const path = `${domain}/reminder/${article_slug}/reject/${review_id}`;

    const res = await handlePutRequest(path, {}, token);
    return res as SuccesType;
};

const hideReview = async (article_slug: string, review_id: number): Promise<SuccesType> => {
    if (!article_slug || review_id === undefined) {
        throw new Error("Faltan campos requeridos: article_slug y review_id");
    }
    const token = useToken();
    const path = `${domain}/reminder/${article_slug}/hide/${review_id}`;

    const res = await handlePutRequest(path, {}, token);
    return res as SuccesType;
};

const reminderService = {
    editEditor,
    assignAssociatedEditor,
    sendToPublication,
    applyAuthorReminder,
    applyReviewReminder,
    unexpireArticle,
    unexpireReview,
    rejectReview,
    hideReview,
};

export default reminderService;